import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { renderAiAssistantBody } from './ai-assistant-markdown';
import {
  AiAssistantChatStore,
  AiAssistantPendingConfirmationItem,
  AiAssistantToolStatus,
  formatRelativeTimestamp,
} from './ai-assistant-chat.store';

interface AiAssistantPendingConfirmationViewItem extends AiAssistantPendingConfirmationItem {
  args: Array<{ key: string; value: string }>;
}

@Component({
  selector: 'tf-ai-assistant',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    TranslateModule,
    ButtonModule,
    FormsModule,
    TextareaModule,
    TooltipModule,
  ],
  templateUrl: './ai-assistant.component.html',
  styleUrl: './ai-assistant.component.scss',
})
export class AiAssistantComponent {
  readonly chat = inject(AiAssistantChatStore);
  private readonly translateService = inject(TranslateService);
  private readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');
  private readonly composerTextarea =
    viewChild<ElementRef<HTMLTextAreaElement>>('composerTextarea');

  readonly closed = output<void>();
  readonly draft = signal('');
  readonly canSend = computed(
    () => this.draft().trim().length > 0 && !this.chat.isSending() && !this.chat.isStreaming()
  );
  readonly showSuggestions = computed(() => !this.chat.hasMessages() && !this.chat.isSending());
  readonly suggestions = [{ icon: 'pi-user', labelKey: 'AI_ASSISTANT.SUGGESTIONS.USER_ROLES' }];

  /**
   * Template-accessible bridge to the feature-local markdown renderer. Kept on the component
   * so the template can call it via `[innerHTML]` without exposing the underlying marked/DOMPurify
   * machinery — the sanitize step happens inside `renderAiAssistantBody` and bypasses Angular's
   * default DomSanitizer warnings since the output is already trusted-by-construction.
   */
  protected renderMarkdown(text: string): string {
    return renderAiAssistantBody(text);
  }
  readonly pendingConfirmationItems = computed<AiAssistantPendingConfirmationViewItem[]>(() =>
    (this.chat.pendingConfirmation()?.items ?? []).map((item) => ({
      ...item,
      args: formatDisplayArgs(item.displayArgs),
    }))
  );

  /**
   * Translation key for the most recent error. Maps known [ProblemDetail.type] URIs to
   * feature-specific i18n keys; falls back to the generic `AI_ASSISTANT.ERROR` for unknown problem
   * types or when the backend didn't attach a problem (generic / unexpected failures).
   */
  readonly errorTranslationKey = computed(() => {
    const problem = this.chat.lastErrorProblem();
    if (!problem) {
      return 'AI_ASSISTANT.ERROR.GENERIC';
    }
    switch (problem.type) {
      case 'https://problems.tourfold.com/ai-assistant/assistant-disabled':
        return 'AI_ASSISTANT.ERROR.ASSISTANT_DISABLED';
      case 'https://problems.tourfold.com/ai-assistant/turn-in-progress':
        return 'AI_ASSISTANT.ERROR.TURN_IN_PROGRESS';
      case 'https://problems.tourfold.com/rate-limit-exceeded':
        return 'AI_ASSISTANT.ERROR.QUOTA_EXCEEDED';
      case 'https://problems.tourfold.com/upstream-service-unavailable':
        return 'AI_ASSISTANT.ERROR.PROVIDER_UNAVAILABLE';
      default:
        return 'AI_ASSISTANT.ERROR.GENERIC';
    }
  });

  constructor() {
    effect(() => {
      this.chat.turns();
      this.chat.pendingConfirmation();
      this.scrollMessagesToBottom();
    });
  }

  onClose(): void {
    this.closed.emit();
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    // While the assistant is still answering, Enter does nothing. Stopping is only via the
    // explicit stop button — Enter is reserved for sending the next message and should not
    // accidentally cancel a stream the user is still reading.
    event.preventDefault();
    if (this.chat.canStop()) {
      return;
    }
    this.sendMessage();
  }

  onStop(): void {
    this.chat.stop();
  }

  onDraftChange(value: string): void {
    this.draft.set(value);
    this.resizeComposerTextarea();
  }

  sendMessage(): void {
    const content = this.draft().trim();
    if (!content || this.chat.isSending()) {
      return;
    }

    this.draft.set('');
    this.resizeComposerTextarea();
    this.chat.sendMessage(content).finally(() => this.scrollMessagesToBottom());
    this.scrollMessagesToBottom();
  }

  sendSuggestion(labelKey: string): void {
    this.draft.set(this.translateService.instant(labelKey));
    this.sendMessage();
  }

  startNewConversation(): void {
    this.chat.startNewConversation();
    this.draft.set('');
    this.resizeComposerTextarea();
    this.scrollMessagesToBottom();
  }

  toggleHistory(): void {
    this.chat.toggleHistory();
  }

  openSession(sessionId: string): void {
    this.chat.openSession(sessionId);
  }

  confirmPendingTool(): void {
    this.chat.confirmPendingTool();
  }

  cancelPendingTool(): void {
    this.chat.cancelPendingTool();
  }

  toolStatusLabelKey(status: AiAssistantToolStatus): string {
    return `AI_ASSISTANT.TOOL.STATUS.${status.toUpperCase()}`;
  }

  formatRelative(timestamp: string | Date | null | undefined): string {
    return formatRelativeTimestamp(timestamp);
  }

  async deleteSession(event: MouseEvent, sessionId: string): Promise<void> {
    // Prevent the parent button (history item) from also firing — we don't want to open the
    // session we're about to delete.
    event.stopPropagation();
    await this.chat.deleteSession(sessionId);
  }

  toolStatusIcon(status: AiAssistantToolStatus): string {
    if (status === 'completed') {
      return 'pi-check';
    }
    if (status === 'declined') {
      return 'pi-ban';
    }
    if (status === 'timeout') {
      return 'pi-clock';
    }
    if (status === 'error') {
      return 'pi-exclamation-circle';
    }
    if (status === 'awaiting_confirmation') {
      return 'pi-shield';
    }
    return 'pi-spin pi-spinner';
  }

  private resizeComposerTextarea(): void {
    queueMicrotask(() => {
      const textarea = this.composerTextarea()?.nativeElement;
      if (!textarea) {
        return;
      }

      const styles = getComputedStyle(textarea);
      const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
      const padding =
        Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
      const maxHeight = lineHeight * 5 + padding;

      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    });
  }

  private scrollMessagesToBottom(): void {
    // requestAnimationFrame runs *after* Angular's next render tick — at queueMicrotask time the
    // DOM still reflects the previous turns, so `scrollHeight` is stale and the scroll is a no-op.
    // We schedule on the next two animation frames defensively: the first lets the @for
    // directive emit new bubbles, the second lets `<markdown>` finish syntax-highlighting and
    // settle on its final height.
    const scroll = (): void => {
      const container = this.messagesContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    };
    requestAnimationFrame(() => {
      scroll();
      requestAnimationFrame(scroll);
    });
  }
}

function formatDisplayArgs(
  args: Record<string, unknown> | null
): Array<{ key: string; value: string }> {
  if (!args) {
    return [];
  }
  return Object.entries(args)
    .filter(([, value]) => value != null)
    .slice(0, 5)
    .map(([key, value]) => ({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    }));
}
